import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Modal, 
  Descriptions,
  message,
  Spin,
  Badge
} from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  FilterOutlined,
  UserOutlined,
  CalendarOutlined,
  CrownOutlined
} from '@ant-design/icons';
import moment from 'moment';
import adminSubscriptionService from '../../services/adminSubscriptionService';

const { Option } = Select;

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    userId: '',
    planId: ''
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await adminSubscriptionService.getAllSubscriptions({
        page: pagination.current - 1,
        size: pagination.pageSize,
        ...(filters.status && { status: filters.status }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.planId && { planId: filters.planId })
      });
      setSubscriptions(data.data.subscriptions || []);
      setPagination(prev => ({
        ...prev,
        total: data.data.totalElements || 0
      }));
    } catch (error) {
      message.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await adminSubscriptionService.getSubscriptionStats();
      setStats(data.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleViewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'EXPIRED':
        return 'red';
      case 'CANCELLED':
        return 'orange';
      case 'SUSPENDED':
        return 'purple';
      case 'PENDING_RENEWAL':
        return 'blue';
      default:
        return 'default';
    }
  };

  const isExpired = (endDate) => {
    return moment(endDate).isBefore(moment());
  };

  const columns = [
  
    {
      title: 'User Email',
      dataIndex: 'userEmail',
      key: 'userEmail',
      width: 170,
    },
    {
      title: 'Plan ID',
      dataIndex: 'planId',
      key: 'planId',
      width: 120,
    },
    {
      title: 'Amount Paid',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      width: 120,
      render: (amount, record) => (
        <span>{amount} {record.currency}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        const expired = isExpired(record.endDate);
        return (
          <Space>
            <Tag color={getStatusColor(status)}>
              {status}
            </Tag>
            {expired && status === 'ACTIVE' && (
              <Badge status="error" text="Expired" />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      render: (date) => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      render: (date) => (
        <span style={{ 
          color: isExpired(date) ? '#ff4d4f' : 'inherit' 
        }}>
          {moment(date).format('DD/MM/YYYY')}
        </span>
      ),
    },
    {
      title: 'Auto Renewal',
      dataIndex: 'autoRenewal',
      key: 'autoRenewal',
      width: 100,
      render: (autoRenewal) => (
        <Tag color={autoRenewal ? 'green' : 'default'}>
          {autoRenewal ? 'Yes' : 'No'}
        </Tag>
      ),
    },
   
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewSubscription(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>Subscription Management</h2>
      
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Subscriptions"
              value={stats.totalSubscriptions || 0}
              prefix={<CrownOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Active Subscriptions"
              value={stats.activeSubscriptions || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Recent (30 days)"
              value={stats.recentSubscriptions || 0}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Expired"
              value={stats.subscriptionsByStatus?.EXPIRED || 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Status Distribution */}
     

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={6}>
            <Input
              placeholder="Search by User ID"
              prefix={<SearchOutlined />}
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="EXPIRED">Expired</Option>
              <Option value="CANCELLED">Cancelled</Option>
              <Option value="SUSPENDED">Suspended</Option>
              <Option value="PENDING_RENEWAL">Pending Renewal</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Input
              placeholder="Plan ID"
              value={filters.planId}
              onChange={(e) => handleFilterChange('planId', e.target.value)}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Button 
              type="primary" 
              icon={<FilterOutlined />}
              onClick={fetchSubscriptions}
            >
              Apply Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="subscriptionId"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} subscriptions`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Subscription Details Modal */}
      <Modal
        title="Subscription Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedSubscription && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Subscription ID" span={2}>
              {selectedSubscription.subscriptionId}
            </Descriptions.Item>
            <Descriptions.Item label="User Email">
              {selectedSubscription.userEmail}
            </Descriptions.Item>
            <Descriptions.Item label="Plan ID">
              {selectedSubscription.planId}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Paid">
              ₹{selectedSubscription.amountPaid} {selectedSubscription.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedSubscription.status)}>
                {selectedSubscription.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {moment(selectedSubscription.startDate).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {moment(selectedSubscription.endDate).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Auto Renewal">
              <Tag color={selectedSubscription.autoRenewal ? 'green' : 'default'}>
                {selectedSubscription.autoRenewal ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {moment(selectedSubscription.createdAt).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {selectedSubscription.updatedAt ? 
                moment(selectedSubscription.updatedAt).format('DD/MM/YYYY HH:mm:ss') : 
                'N/A'
              }
            </Descriptions.Item>
            {selectedSubscription.cancelledAt && (
              <Descriptions.Item label="Cancelled At">
                {moment(selectedSubscription.cancelledAt).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
            )}
            {selectedSubscription.cancellationReason && (
              <Descriptions.Item label="Cancellation Reason" span={2}>
                {selectedSubscription.cancellationReason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AdminSubscriptions;
